import { FC } from 'react';
import { JobDetailResponse } from '@doda/api-client/aspida/@types';
import {
  reWriteDate,
  replaceEmploymentType,
  createDescription,
  createAdressArray,
  createTitle,
  createCompanyName,
  createIndustryName,
  createOccupationalName,
  createWorkHourText,
  isDirectApply,
  createJobBenefitsText,
} from '@doda/common/src/logics/jobSearchDetail/fixData';
import { DODA_SITE_DOMAIN } from '@doda/common/src/constants/urls';
import { unicodeEscape } from '../../../logics/jobSearchDetail/unicodeEscape';

/**
 * Props
 * @param {JobDetailResponse} jobSearchDetail 求人詳細APIレスポンス値
 */
export interface Props {
  jobSearchDetail: JobDetailResponse;
}

/**
 * JsonDataの型
 */
export interface JsonData {
  '@context': string;
  '@type': string;
  datePosted: string | null;
  description: string | null;
  industry: string;
  occupationalCategory: string;
  workHours: string;
  jobBenefits?: string;
  employmentType:
    | string
    | ('FULL_TIME' | 'CONTRACTOR' | 'TEMPORARY' | 'OTHER')[]
    | null;
  directApply?: boolean;
  hiringOrganization: {
    '@type': string;
    logo: string;
    name?: string | null;
    sameAs?: string;
  };
  jobLocation: {
    '@type': string;
    address:
      | {
          '@type': string;
          addressCountry: string | null;
          addressLocality: string | undefined;
          addressRegion: string | undefined;
        }[]
      | undefined;
  };
  title;
  validThrough?: string | null;
  url?: string;
}

/**
 * 求人住所のモデル
 */
export interface JobAddress {
  addressCountry: string | null;
  addressLocality: string | undefined;
  addressRegion: string | undefined;
}

/**
 * JobPosting構造化データコンポーネント
 * @param {JobDetailResponse} jobSearchDetail 求人詳細APIレスポンス値
 */
export const StructuredData: FC<Props> = ({ jobSearchDetail }) => {
  const { area, job, locations, searchJobType, industry } = jobSearchDetail;

  const datePosted = reWriteDate(job?.publishingSchedulePeriodStartDate ?? '');
  const dateEnd = reWriteDate(job?.publishingSchedulePeriodEndDate ?? '');
  const employmentType = replaceEmploymentType(
    job?.recruit?.formDivision ?? ''
  );
  const hiringOrganization = createCompanyName(
    job?.corporateName ?? '',
    job?.wcnsId ?? ''
  );
  const address = createAdressArray(
    locations,
    job?.googleForJobs?.prefectureNameList,
    area?.id
  );
  const title = createTitle(job?.googleForJobs || {}, searchJobType);

  const description = unicodeEscape(createDescription(jobSearchDetail));
  // 参考::  https://developers.google.com/search/docs/advanced/structured-data/job-posting?hl=ja

  const domain =
      DODA_SITE_DOMAIN[process.env.APP_ENV as keyof typeof DODA_SITE_DOMAIN] ||
      DODA_SITE_DOMAIN.Production;
  const industryName = createIndustryName(industry);
  const occupationalCategory = createOccupationalName(searchJobType);
  const workHours = createWorkHourText(job?.recruit);
  const directApply = isDirectApply(job);
  const jobBenefits = createJobBenefitsText(job?.recruit);
  const sameAs = job?.businessOutline?.homePageLink || undefined;
  const url = `${domain}/DodaFront/View/JobSearchDetail/j_jid__${job?.jid || ''}/`;

  const jsonData: JsonData = {
    '@context': 'https://schema.org',
    '@type': 'JobPosting',
    datePosted,
    description,
    industry: industryName,
    occupationalCategory,
    workHours,
    jobBenefits,
    employmentType,
    directApply,
    hiringOrganization: {
      '@type': 'Organization',
      logo: 'https://doda.jp/cmn_web/img/gfj/doda_logo_gfj_300x300.png',
      name: hiringOrganization,
      sameAs
    },
    jobLocation: {
      '@type': 'Place',
      address,
    },
    title,
    validThrough: dateEnd,
    url
  };

  if (!directApply) delete jsonData.directApply;
  if (!jobBenefits) delete jsonData.jobBenefits;

  // 必須項目に設定値がない場合は、nullを返す
  if (
    !datePosted ||
    !dateEnd ||
    !hiringOrganization ||
    !address?.length ||
    !title ||
    !description
  )
    return null;

  return (
    <>
      {jsonData ? (
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(jsonData),
          }}
        />
      ) : null}
    </>
  );
};
